// @flow

import React, { type Element, useState } from 'react'
import {
  Container,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogContent,
  Typography,
} from '@material-ui/core'
import { MuiThemeProvider, styled, makeStyles } from '@material-ui/core/styles'

import theme from '../../../theme'

import CustomStepper from '../../Common/CustomStepper'
import AlertError from '../../Common/AlertError'
import CreateLoginForm from '../CreateLoginForm'
import EmailConfirmation from '../EmailConfirmation'
import FinalDetails from '../FinalDetails'
import urlParam from '../../../utils/urlParam'
import Banner from '../SideBanner/Banner'
import parse from 'html-react-parser'
import UnderlineImage from '../../../../assets/images/banner/underline.png'
import Logo from '../../../../assets/images/banner/banner-logo-2022.png'

export type Speciality = {
  id: number,
  title: string,
  province: string,
}

export type UrlParams = {
  promoCode?: string,
  billShareGuid?: string,
  billShareToken?: string,
}

type RegistrationContainerProps = {|
  activeStepIndex: number,
  plan: {|
    id: number,
    name: string,
    description: string,
    tag: string,
    signUpDescription: string,
    hasExtraSignupTerms: boolean,
    termsAndConditions: string,
    title: Array<string>,
  |},
  termsAndConditionsUrls: {| url: string, pdfUrl: string |},
  allSpecialities: Array<Speciality>,
  selectedCategory: string,
  selectedUserInfo: {| firstName: string, lastName: string, email: string |},
  serverErrors?: { [string]: Array<string> },
  urlParams: UrlParams,
  planBlacklistedForProvince: Array<string>,
  lfpFeatureFlag: boolean,
|}

const steps = ['Create login', 'Confirm email', 'Final details']

const useStyles = makeStyles({
  heading: {
    fontSize: '32px',
    marginBottom: '20px',
    fontWeight: '500',
  },
  '@media (max-width: 425px)': {
    heading: {
      fontSize: '22px',
    },
  },
  body: {
    color: '#333333',
  },
  '@media (min-width: 992px)': {
    stepperPadding: {
      paddingTop: '52px',
    },
  },
  title: {
    padding: '24px',
  },
  wrapper: {
    margin: theme.spacing(3),
    position: 'relative',
    alignSelf: 'end',
  },
  fillScreen: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: '100%',
      position: 'fixed',
      margin: 0,
    },
  },
  '@media (max-width: 992px)': {
    fullWidthButton: {
      'flex-grow': '0',
      'max-width': '100%',
      'flex-basis': '100%',
    },
    wrapper: {
      marginLeft: '20px',
      marginRight: '20px',
    },
  },
})

const MobileHeader = styled('div')({
  background: '#EFEDE7',
  height: '64px',
  padding: '20px',
})

const ImageLogo = styled('img')({
  width: '72px',
  height: '24px',
})

function RegistrationContainer({
  activeStepIndex,
  plan,
  termsAndConditionsUrls,
  allSpecialities,
  selectedCategory,
  selectedUserInfo,
  urlParams,
  serverErrors,
  planBlacklistedForProvince,
  lfpFeatureFlag,
}: RegistrationContainerProps): Element<typeof MuiThemeProvider> {
  const classes = useStyles()
  const [activeStep, setActiveStep] = useState(activeStepIndex)
  const [errors, setServerErrors] = useState(serverErrors)
  const [email, storeEmail] = useState('')
  const [registrationUuid, storeRegistrationUuid] = useState(urlParam('registration-id'))
  const [activationCode, storeActivationCode] = useState(urlParam('activation-code'))
  const [isOpen, setIsOpen] = React.useState(false)

  function openModal() {
    setIsOpen(true)
  }

  function handleClose() {
    setIsOpen(false)
  }

  function handleServerErrors(errors: { [string]: Array<string> }): void {
    setServerErrors(errors)
  }

  function handleNext() {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const renderPageStep = ():
    | Element<typeof Grid>
    | Element<typeof EmailConfirmation>
    | Element<typeof FinalDetails>
    | null => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h3" className={classes.heading}>
                {plan.title[0]}
                <span style={{ position: 'relative' }}>
                  {plan.title[1]}
                  <img src={UnderlineImage} className={'underline-image'} alt="yellow underline" />
                </span>
              </Typography>
              <Typography variant="body1" className={classes.body}>
                {parse(plan.signUpDescription)}
                {plan.hasExtraSignupTerms && (
                  <a role="button" onClick={() => openModal()}>
                    <span style={{ fontWeight: 'bold' }}>Terms apply.</span>
                  </a>
                )}
              </Typography>
              <Dialog
                classes={{ paper: classes.fillScreen }}
                open={isOpen}
                onClose={handleClose}
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
                fullWidth
                maxWidth="md"
              >
                <DialogTitle id="scroll-dialog-title" className={classes.title}>
                  <Typography variant="h4">{'Terms Apply'}</Typography>
                </DialogTitle>
                <DialogContent>
                  <DialogContentText
                    id="scroll-dialog-description"
                    tabIndex={-1}
                    className={classes.body}
                    variant="body1"
                  >
                    {parse(plan.termsAndConditions)}
                  </DialogContentText>
                </DialogContent>
                <Grid container item style={theme.rightAlignItem}>
                  <Grid item className={classes.fullWidthButton}>
                    <div className={classes.wrapper}>
                      <Button
                        onClick={handleClose}
                        color="primary"
                        color="primary"
                        variant="contained"
                        fullWidth
                      >
                        GOT IT
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </Dialog>
            </Grid>
            <CreateLoginForm
              storeEmail={storeEmail}
              storeRegistrationUuid={storeRegistrationUuid}
              onServerErrorsCallback={handleServerErrors}
              goNextPageCallback={handleNext}
              termsAndConditionsUrls={termsAndConditionsUrls}
              selectedUserInfo={selectedUserInfo}
              selectedPlan={plan}
            />
          </Grid>
        )
      case 1:
        return (
          <EmailConfirmation
            email={email}
            registrationUuid={registrationUuid}
            storeActivationCode={storeActivationCode}
            goNextPageCallback={handleNext}
          />
        )
      case 2:
        return (
          <FinalDetails
            registrationUuid={registrationUuid}
            activationCode={activationCode}
            allSpecialities={allSpecialities}
            selectedCategory={selectedCategory}
            urlParams={urlParams}
            planBlacklistedForProvince={planBlacklistedForProvince}
            lfpFeatureFlag={lfpFeatureFlag}
          />
        )
      default:
        return null
    }
  }

  return (
    <MuiThemeProvider theme={theme}>
      <Grid container>
        <Grid item xs={4} className={'banner-mobileport'}>
          <Banner />
        </Grid>
        <Grid item xs={8} className={'form-mobileport'}>
          <MobileHeader className={'header-mobile'}>
            <a data-turbolinks="false" href="/">
              <ImageLogo src={Logo} />
            </a>
          </MobileHeader>
          <Container maxWidth="sm" className={classes.stepperPadding}>
            <CustomStepper activeStep={activeStep} steps={steps} />
          </Container>
          <Container maxWidth="sm">
            <Grid item xs={12}>
              {errors && <AlertError errors={errors} />}
            </Grid>
            {renderPageStep()}
          </Container>
        </Grid>
      </Grid>
    </MuiThemeProvider>
  )
}

export default RegistrationContainer
